import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { navigate } from "../../lib/router";
import { PageHead } from "../../components/Shell";
import { ErrorBox, Loading, Panel, Seg } from "../../components/ui";
import { Kpi } from "../../components/charts";
import { IconSearch } from "../../components/icons";
import { useMembers, type Member } from "./Team";

const CARD_W = 228, CARD_H = 58, COL_GAP = 64, ROW_GAP = 12;
const ROW_H = CARD_H + ROW_GAP;
const COL_W = CARD_W + COL_GAP;

type Node = {
  m: Member; kids: Node[]; depth: number; activeBelow: number; gens: number; inactive: boolean;
  x: number; y: number; hiddenKids: number;
};

const People = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
    <circle cx="9" cy="8" r="3.2" /><path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" /><circle cx="17" cy="9" r="2.6" /><path d="M16 14.2c2.9.2 5 2.6 5 5.8" />
  </svg>
);

export default function TeamTree() {
  const members = useMembers();
  const [showAll, setShowAll] = useState(false);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [zoom, setZoom] = useState(1);
  const [focus, setFocus] = useState<string | null>(null);
  const [view, setView] = useState<"chart" | "list">("chart");
  const scroller = useRef<HTMLDivElement>(null);
  const all = members.data ?? [];

  // Who is in the picture: active members, plus anyone above an active member (shown grey if no longer active)
  const { roots, byId, maxDepth, leaders, stats } = useMemo(() => {
    const byIdAll = new Map(all.map((m) => [m.member_id, m]));
    const keep = new Set<string>();
    for (const m of all) {
      if (m.status !== "Active" && !showAll) continue;
      let cur: Member | undefined = m;
      const seen = new Set<string>();
      while (cur && !seen.has(cur.member_id)) { seen.add(cur.member_id); keep.add(cur.member_id); cur = cur.recruiter_id ? byIdAll.get(cur.recruiter_id) : undefined; }
    }
    const list = all.filter((m) => keep.has(m.member_id));
    const childrenOf = new Map<string, Member[]>();
    for (const m of list) if (m.recruiter_id && keep.has(m.recruiter_id)) childrenOf.set(m.recruiter_id, [...(childrenOf.get(m.recruiter_id) ?? []), m]);
    // The SMD (root) is the top person with the biggest team; anyone else without a known recruiter hangs under them
    const tops = list.filter((m) => !m.recruiter_id || !keep.has(m.recruiter_id));
    const root = tops.slice().sort((a, b) => (childrenOf.get(b.member_id)?.length ?? 0) - (childrenOf.get(a.member_id)?.length ?? 0))[0];
    if (root) for (const m of tops) if (m !== root) childrenOf.set(root.member_id, [...(childrenOf.get(root.member_id) ?? []), m]);
    const byId = new Map<string, Node>();
    const build = (m: Member, depth: number, path: Set<string>): Node => {
      const kidsRaw = path.has(m.member_id) ? [] : (childrenOf.get(m.member_id) ?? []);
      const p2 = new Set(path).add(m.member_id);
      const kids = kidsRaw.map((k) => build(k, depth + 1, p2));
      const activeBelow = kids.reduce((s, k) => s + k.activeBelow + (k.inactive ? 0 : 1), 0);
      const gens = Math.max(0, ...kids.map((k) => (!k.inactive || k.activeBelow > 0 ? 1 + k.gens : 0)));
      kids.sort((a, b) => b.activeBelow - a.activeBelow || Number(a.inactive) - Number(b.inactive) || (a.m.recruit_date ?? "").localeCompare(b.m.recruit_date ?? "") || a.m.full_name.localeCompare(b.m.full_name));
      const n: Node = { m, kids, depth, activeBelow, gens, inactive: m.status !== "Active", x: 0, y: 0, hiddenKids: 0 };
      byId.set(m.member_id, n);
      return n;
    };
    const roots = root ? [build(root, 0, new Set())] : [];
    const nodes = [...byId.values()];
    const leaders = nodes.filter((n) => n.kids.some((k) => !k.inactive) || n.activeBelow > 0)
      .map((n) => ({ n, direct: n.kids.filter((k) => !k.inactive).length }))
      .sort((a, b) => b.n.activeBelow - a.n.activeBelow || b.direct - a.direct || a.n.m.full_name.localeCompare(b.n.m.full_name));
    const active = all.filter((m) => m.status === "Active");
    return {
      roots, byId, leaders,
      maxDepth: Math.max(0, ...nodes.map((n) => n.depth)),
      stats: { active: active.length, licensed: active.filter((m) => m.licensed).length, gens: Math.max(0, ...roots.map((r) => r.gens)), leaders: leaders.length },
    };
  }, [all, showAll]);

  // Search: matching cards + their upline path
  const query = q.trim().toLowerCase();
  const matches = useMemo(() => {
    if (!query) return new Set<string>();
    return new Set([...byId.values()].filter((n) => `${n.m.full_name} ${n.m.agent_code ?? ""}`.toLowerCase().includes(query)).map((n) => n.m.member_id));
  }, [query, byId]);
  const pathIds = useMemo(() => {
    const ids = new Set<string>();
    const seeds = focus ? [focus] : [...matches];
    for (const id of seeds) {
      let cur = byId.get(id);
      while (cur) { ids.add(cur.m.member_id); cur = cur.m.recruiter_id ? byId.get(cur.m.recruiter_id) : undefined; }
    }
    return ids;
  }, [matches, focus, byId]);

  // Layout (tidy tree: leaves stack down, parents sit in the middle of their children)
  const layout = useMemo(() => {
    let row = 0;
    const placed: Node[] = [];
    const place = (n: Node): number => {
      n.x = n.depth * COL_W;
      const id = n.m.member_id;
      const open = !collapsed.has(id) || (pathIds.has(id) && !matches.has(id) && focus !== id);
      n.hiddenKids = open ? 0 : n.kids.length;
      placed.push(n);
      if (!n.kids.length || !open) { n.y = row++ * ROW_H; return n.y; }
      const ys = n.kids.map(place);
      n.y = (ys[0] + ys[ys.length - 1]) / 2;
      return n.y;
    };
    roots.forEach((r, i) => { if (i) row += 0.6; place(r); });
    const visible = new Set(placed.map((n) => n.m.member_id));
    return { placed, visible, height: Math.max(row, 1) * ROW_H, width: (maxDepth + 1) * COL_W - COL_GAP };
  }, [roots, collapsed, pathIds, maxDepth, matches, focus]);

  // Scroll the first match / focused person into view
  useEffect(() => {
    const id = focus ?? [...matches][0];
    if (!id || !scroller.current) return;
    const n = byId.get(id);
    if (!n || !layout.visible.has(id)) return;
    const el = scroller.current;
    el.scrollTo({ left: Math.max(0, n.x * zoom - el.clientWidth / 2 + (CARD_W * zoom) / 2), top: Math.max(0, n.y * zoom - el.clientHeight / 2 + 40), behavior: "smooth" });
  }, [focus, matches, layout, zoom, byId]);

  // Open on the root (SMD), vertically centred, the first time the chart is drawn
  const opened = useRef(false);
  useEffect(() => {
    if (opened.current || view !== "chart" || !scroller.current || !roots[0] || focus || query) return;
    const el = scroller.current, r = roots[0];
    el.scrollTo({ left: 0, top: Math.max(0, r.y * zoom - el.clientHeight / 2 + 60) });
    opened.current = true;
  }, [layout, view, roots, zoom, focus, query]);

  const toggle = (id: string) => setCollapsed((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const collapseTo = (depth: number | null) => setCollapsed(new Set(depth == null ? [] : [...byId.values()].filter((n) => n.depth >= depth && n.kids.length).map((n) => n.m.member_id)));
  const hasHighlight = pathIds.size > 0;

  const maxBelow = Math.max(1, ...leaders.map((l) => l.n.activeBelow));
  const genLabel = (d: number) => (d === 0 ? (roots[0]?.m.full_name.split(" ")[0] ?? "Leader") : `Generation ${d}`);

  return (
    <>
      <PageHead title="Team tree" sub="Who recruited whom. Active team members, read left to right: each person, then the people they recruited." />
      <div className="kpis">
        <Kpi dark label="Active team" value={stats.active} />
        <Kpi label="Licensed" value={stats.licensed} unit={`of ${stats.active}`} actual={stats.licensed} target={stats.active || undefined} />
        <Kpi label="Generations deep" value={stats.gens} />
        <Kpi label="Leaders with a team" value={stats.leaders} sub="Have at least one active recruit" />
      </div>
      <ErrorBox error={members.error} />
      <div className="tree-layout">
        <Panel className="tree-panel">
          <div className="tree-bar">
            <div className="search" style={{ flex: "1 1 220px", maxWidth: 320 }}>
              <IconSearch />
              <input className="input" placeholder="Find someone by name or code" value={q} onChange={(e) => { setQ(e.target.value); setFocus(null); }} />
            </div>
            <Seg value={view} onChange={setView} options={[{ value: "chart", label: "Chart" }, { value: "list", label: "List" }]} />
            <Seg value={collapsed.size === 0 ? "all" : "custom"} onChange={(v) => collapseTo(v === "all" ? null : v === "g1" ? 1 : 2)}
              options={[{ value: "g1", label: "Gen 1" }, { value: "g2", label: "To Gen 2" }, { value: "all", label: "Everyone" }]} />
            <label className="check small"><input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} /> Include terminated</label>
            <div className="zoom" role="group" aria-label="Zoom">
              <button className="icon-btn" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(2)))} aria-label="Zoom out">−</button>
              <span>{Math.round(zoom * 100)}%</span>
              <button className="icon-btn" onClick={() => setZoom((z) => Math.min(1.3, +(z + 0.1).toFixed(2)))} aria-label="Zoom in">+</button>
            </div>
          </div>
          <div className="tree-legend">
            <span><i className="lg licensed" />Licensed</span>
            <span><i className="lg pending" />Not yet licensed</span>
            <span><i className="lg gone" />Upline no longer active</span>
            <span><People /> active people below them</span>
            {query && <span className="muted">{matches.size} found</span>}
          </div>
          {members.loading && !members.data ? <Loading rows={8} /> : view === "list" ? (
            <TreeList placed={layout.placed} byId={byId} hit={matches} lit={pathIds} onToggle={toggle} />
          ) : (
            <div className="tree-scroll" ref={scroller}>
              <div className="tree-gens" style={{ width: layout.width * zoom + 48 }}>
                {Array.from({ length: maxDepth + 1 }, (_, d) => (
                  <span key={d} style={{ left: 24 + d * COL_W * zoom, width: CARD_W * zoom }}>{genLabel(d)}</span>
                ))}
              </div>
              <div className="tree-canvas" style={{ width: layout.width * zoom + 48, height: layout.height * zoom + 40 }}>
                <div style={{ transform: `scale(${zoom})`, transformOrigin: "0 0", position: "absolute", left: 24, top: 16, width: layout.width, height: layout.height }}>
                  <svg className="tree-lines" width={layout.width} height={layout.height} aria-hidden="true">
                    {layout.placed.filter((n) => n.kids.length && !n.hiddenKids).flatMap((n) => n.kids.map((k) => {
                      const x1 = n.x + CARD_W, y1 = n.y + CARD_H / 2, x2 = k.x, y2 = k.y + CARD_H / 2, mx = x1 + COL_GAP / 2;
                      const r = Math.min(10, Math.abs(y2 - y1) / 2);
                      const d = y1 === y2 ? `M${x1},${y1} H${x2}` : `M${x1},${y1} H${mx - r} Q${mx},${y1} ${mx},${y1 + Math.sign(y2 - y1) * r} V${y2 - Math.sign(y2 - y1) * r} Q${mx},${y2} ${mx + r},${y2} H${x2}`;
                      const lit = hasHighlight && pathIds.has(k.m.member_id) && pathIds.has(n.m.member_id);
                      return <path key={k.m.member_id} d={d} className={clsx(lit && "lit", k.inactive && "gone")} />;
                    }))}
                  </svg>
                  {layout.placed.map((n) => {
                    const m = n.m;
                    const lit = hasHighlight && pathIds.has(m.member_id);
                    const hit = matches.has(m.member_id) || focus === m.member_id;
                    return (
                      <div key={m.member_id} className={clsx("tcard", n.depth === 0 ? "root" : n.inactive ? "gone" : m.licensed ? "licensed" : "pending", hasHighlight && !lit && "dim", hit && "hit")}
                        style={{ left: n.x, top: n.y, width: CARD_W, height: CARD_H }}>
                        <button className="tc-main" onClick={() => navigate(`/team/members/${m.member_id}`)} title={`Open ${m.full_name}`}>
                          <b>{m.full_name}{n.inactive && n.depth > 0 ? <em> (not active)</em> : null}</b>
                          <span>
                            {m.agent_code ?? "no code"}
                            {m.recruit_date && n.depth > 0 ? <> · {dayjs(m.recruit_date).format("MMM YYYY")}</> : null}
                            {n.activeBelow > 0 && <> · <People /> {n.activeBelow}</>}
                          </span>
                        </button>
                        {n.kids.length > 0 && (
                          <button className={clsx("tc-toggle", n.hiddenKids && "closed")} onClick={() => toggle(m.member_id)}
                            aria-label={n.hiddenKids ? `Show ${n.kids.length} recruits` : "Hide recruits"} title={n.hiddenKids ? `Show ${n.kids.length} recruits` : "Hide recruits"}>
                            {n.hiddenKids ? n.kids.length : "−"}
                          </button>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
        </Panel>

        <Panel title="Leaders" hint="Active people only · click to find them in the tree" className="leaders-panel">
          <div className="table-wrap">
            <table className="data leaders">
              <thead><tr><th>Leader</th><th className="num">Direct</th><th style={{ width: 110 }}>Downline</th><th className="num">Gens</th></tr></thead>
              <tbody>
                {leaders.map(({ n, direct }) => (
                  <tr key={n.m.member_id} data-click aria-selected={focus === n.m.member_id} onClick={() => { setQ(""); setFocus(focus === n.m.member_id ? null : n.m.member_id); setCollapsed((s) => { const c = new Set(s); let cur: Node | undefined = n; while (cur) { c.delete(cur.m.member_id); cur = cur.m.recruiter_id ? byId.get(cur.m.recruiter_id) : undefined; } return c; }); }}>
                    <td><span className={clsx("ldot", n.inactive ? "gone" : n.m.licensed ? "licensed" : "pending")} />{n.m.full_name}{n.inactive && <span className="muted small"> (not active)</span>}</td>
                    <td className="num">{direct}</td>
                    <td><div className="dbar"><i style={{ width: `${(n.activeBelow / maxBelow) * 100}%` }} /><span>{n.activeBelow}</span></div></td>
                    <td className="num">{n.gens}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </>
  );
}

/** The v1.3 "live list" view: outline with ├ └ guides plus the detail columns. */
function TreeList({ placed, byId, hit, lit, onToggle }: { placed: Node[]; byId: Map<string, Node>; hit: Set<string>; lit: Set<string>; onToggle: (id: string) => void }) {
  const isLast = (n: Node) => { const p = n.m.recruiter_id ? byId.get(n.m.recruiter_id) : undefined; const sib = p ? p.kids : [n]; return sib[sib.length - 1] === n; };
  const guides = (n: Node) => {
    const out: ("pipe" | "blank")[] = [];
    let cur = n.m.recruiter_id ? byId.get(n.m.recruiter_id) : undefined;
    while (cur && cur.depth > 0) { out.unshift(isLast(cur) ? "blank" : "pipe"); cur = cur.m.recruiter_id ? byId.get(cur.m.recruiter_id) : undefined; }
    return out;
  };
  return (
    <div className="table-wrap tree-list">
      <table className="data">
        <thead><tr><th>Who recruited whom</th><th>Code</th><th>Recruited by</th><th>Joined</th><th>Licensed</th><th>Status</th><th className="num">Direct team</th><th className="num">Total downline</th></tr></thead>
        <tbody>
          {placed.map((n) => {
            const m = n.m;
            return (
              <tr key={m.member_id} className={clsx(hit.has(m.member_id) && "hit", lit.size > 0 && !lit.has(m.member_id) && "dim")} data-click onClick={() => navigate(`/team/members/${m.member_id}`)}>
                <td className="tl-name">
                  {n.depth > 0 && guides(n).map((g, i) => <span key={i} className={`g ${g}`} />)}
                  {n.depth > 0 && <span className={clsx("g", isLast(n) ? "elbow" : "tee")} />}
                  {n.kids.length > 0 && <button className="tl-tog" onClick={(e) => { e.stopPropagation(); onToggle(m.member_id); }} aria-label="Show or hide recruits">{n.hiddenKids ? "+" : "−"}</button>}
                  <span className={clsx("ldot", n.depth === 0 ? "root" : n.inactive ? "gone" : m.licensed ? "licensed" : "pending")} />
                  <b className={clsx(n.inactive && "muted")}>{m.full_name}</b>
                </td>
                <td className="nowrap">{n.depth === 0 ? "SMD" : m.agent_code}</td>
                <td>{n.depth === 0 ? "" : m.recruiter}</td>
                <td className="nowrap">{m.recruit_date && n.depth > 0 ? dayjs(m.recruit_date).format("MMM D, YYYY") : ""}</td>
                <td>{m.licensed ? "Yes" : "No"}</td>
                <td>{n.depth === 0 ? "Root" : m.status}</td>
                <td className="num">{n.kids.filter((k) => !k.inactive).length || ""}</td>
                <td className="num">{n.activeBelow || ""}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
